import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Boxes, Gavel, ShoppingBag, X, Package, Ruler, Plus, Pencil, Trash2, Radio, ImagePlus, Truck, Upload, FileSpreadsheet, Download, Link2, RefreshCw, Store } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty, money } from "@/components/common";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

async function fileToScaledDataUrl(file, max = 1000) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function ProductModal({ product, onClose, onSaved }) {
  const editing = !!product;
  const p = product || {};
  const pk = p.package || {};
  const [form, setForm] = useState({
    title: p.title || "", description: p.description || "", price: p.price || "",
    qty: p.qty ?? 1, type: p.type || "buynow", image: p.image || null, sku: p.sku || "",
    requires_shipping: pk.requires_shipping ?? true,
    free_shipping: pk.free_shipping ?? false,
    flat_shipping_fee: pk.flat_fee ?? "",
    weight: pk.weight ?? 1, length: pk.length ?? 8, width: pk.width ?? 6, height: pk.height ?? 4,
  });
  const [saving, setSaving] = useState(false);
  const imgInput = useRef(null);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image");
    try { const img = await fileToScaledDataUrl(f); setForm((s) => ({ ...s, image: img })); } catch { toast.error("Couldn't read image"); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Add a title");
    if (Number(form.price) <= 0) return toast.error("Set a price greater than 0");
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(), description: form.description.trim(),
        price: Number(form.price), qty: Number(form.qty) || 0, type: form.type, image: form.image,
        sku: form.sku.trim() || null,
        requires_shipping: form.requires_shipping,
        free_shipping: form.requires_shipping ? form.free_shipping : false,
        flat_shipping_fee: form.requires_shipping && !form.free_shipping && Number(form.flat_shipping_fee) > 0
          ? Number(form.flat_shipping_fee) : null,
        weight: Number(form.weight) || 1, length: Number(form.length) || 8,
        width: Number(form.width) || 6, height: Number(form.height) || 4,
      };
      if (editing) await api.patch(`/seller/products/${product.id}`, body);
      else await api.post("/seller/products", body);
      toast.success(editing ? "Item updated" : "Item added to inventory");
      onSaved();
    } catch (err) { toast.error(err.message || "Could not save"); } finally { setSaving(false); }
  };

  const dim = (label, k) => (
    <div>
      <label className="ss-label">{label}</label>
      <div className="ss-field" style={{ height: 44 }}>
        <input type="number" min="0" step="0.1" value={form[k]} onChange={set(k)} disabled={!form.requires_shipping} data-testid={`product-${k}`} />
      </div>
    </div>
  );

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <form className="ss-modal" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="product-modal" style={{ maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 20 }}>{editing ? "Edit item" : "New inventory item"}</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="product-close"><X size={18} /></button>
        </div>

        <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
          <input ref={imgInput} type="file" accept="image/*" style={{ display: "none" }} onChange={onImage} data-testid="product-image-input" />
          <button type="button" onClick={() => imgInput.current?.click()} data-testid="product-image-upload"
            style={{ width: 88, height: 88, flexShrink: 0, borderRadius: 12, border: "2px dashed var(--line)", background: form.image ? `center/cover no-repeat url(${form.image})` : "transparent", display: "grid", placeItems: "center", cursor: "pointer", color: "var(--muted)" }}>
            {!form.image && <ImagePlus size={22} />}
          </button>
          <div style={{ flex: 1 }}>
            <label className="ss-label">Title</label>
            <div className="ss-field"><input placeholder="e.g. Handwoven prayer mat" value={form.title} onChange={set("title")} data-testid="product-title" /></div>
          </div>
        </div>

        <label className="ss-label">Description</label>
        <div className="ss-field" style={{ height: "auto", padding: 0, marginBottom: 12 }}>
          <textarea placeholder="Describe the item, condition, size, etc." value={form.description} onChange={set("description")} rows={3} style={{ width: "100%", border: 0, background: "transparent", padding: 12, resize: "vertical", font: "inherit", color: "inherit", outline: "none" }} data-testid="product-description" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginBottom: 12 }}>
          <div>
            <label className="ss-label">Price ($)</label>
            <div className="ss-field" style={{ height: 44 }}><input type="number" min="0" step="0.01" placeholder="0.00" value={form.price} onChange={set("price")} data-testid="product-price" /></div>
          </div>
          <div>
            <label className="ss-label">Quantity</label>
            <div className="ss-field" style={{ height: 44 }}><input type="number" min="0" step="1" value={form.qty} onChange={set("qty")} data-testid="product-qty" /></div>
          </div>
          <div>
            <label className="ss-label">Sell as</label>
            <div className="ss-field" style={{ height: 44, padding: 0 }}>
              <select value={form.type} onChange={set("type")} style={{ width: "100%", border: 0, background: "transparent", padding: "0 12px", font: "inherit", color: "inherit", outline: "none" }} data-testid="product-type">
                <option value="buynow">Buy now</option>
                <option value="auction">Auction</option>
              </select>
            </div>
          </div>
        </div>

        <label className="ss-label" style={{ marginTop: 12 }}>SKU (optional — used to match items when you re-import or sync)</label>
        <div className="ss-field" style={{ height: 44, marginBottom: 12 }}><input placeholder="e.g. OUD-12" value={form.sku} onChange={set("sku")} data-testid="product-sku" /></div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 12, background: "var(--gold-soft)", cursor: "pointer", marginBottom: 12 }}>
          <input type="checkbox" checked={form.requires_shipping} onChange={(e) => setForm((s) => ({ ...s, requires_shipping: e.target.checked }))} data-testid="product-requires-shipping" />
          <div>
            <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}><Package size={15} /> Requires shipping</div>
            <div className="ss-muted" style={{ fontSize: 12 }}>Turn off for digital goods or in-person pickup.</div>
          </div>
        </label>

        {form.requires_shipping && (
          <>
            <label className="ss-card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, cursor: "pointer", marginBottom: 12 }}>
              <input type="checkbox" checked={form.free_shipping} onChange={(e) => setForm((s) => ({ ...s, free_shipping: e.target.checked }))} data-testid="product-free-shipping" />
              <div>
                <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}><Truck size={15} /> Free shipping for buyers</div>
                <div className="ss-muted" style={{ fontSize: 13 }}>Buyers pay $0 shipping — you cover the label cost.</div>
              </div>
            </label>
            {!form.free_shipping && (
              <div className="ss-card" style={{ padding: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><Truck size={15} /> Charge a flat shipping fee</div>
                <div className="ss-muted" style={{ fontSize: 13, marginBottom: 8 }}>Buyers pay this exact amount (a $2 platform fee is added). Leave blank to use live carrier rates.</div>
                <div className="ss-field" style={{ height: 44, maxWidth: 200 }}>
                  <span className="ss-muted" style={{ paddingLeft: 4 }}>$</span>
                  <input type="number" min="0" step="0.01" placeholder="e.g. 5.99" value={form.flat_shipping_fee} onChange={set("flat_shipping_fee")} data-testid="product-flat-fee" />
                </div>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 800, fontSize: 14, marginBottom: 8 }}><Ruler size={15} /> Package weight & dimensions</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}>
              {dim("Weight (lb)", "weight")}{dim("Length (in)", "length")}{dim("Width (in)", "width")}{dim("Height (in)", "height")}
            </div>
          </>
        )}

        <button className="ss-btn ss-btn-primary ss-btn-block" style={{ marginTop: 16 }} disabled={saving} data-testid="save-product">{saving ? "Saving…" : editing ? "Save changes" : "Add to inventory"}</button>
      </form>
    </div>
  );
}

function AddToShowModal({ product, shows, onClose, onAdded }) {
  const [showId, setShowId] = useState(shows[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!showId) return;
    setBusy(true);
    try {
      await api.post(`/shows/${showId}/lots`, { product_id: product.id, qty: 1 });
      const s = shows.find((x) => x.id === showId);
      toast.success(`Added "${product.title}" to ${s?.title || "show"}`);
      onAdded();
    } catch (e) { toast.error(e.message || "Could not add to show"); setBusy(false); }
  };
  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="add-to-show-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>Add to a show</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="add-to-show-close"><X size={18} /></button>
        </div>
        <p className="ss-muted" style={{ fontSize: 13, marginBottom: 14 }}>List <b>{product.title}</b> ({money(product.price)}) in one of your shows.</p>
        {shows.length === 0 ? (
          <div className="ss-muted" style={{ fontSize: 14, marginBottom: 16 }}>You have no live or scheduled shows. Start a show first, then add items to it.</div>
        ) : (
          <>
            <label className="ss-label">Choose show</label>
            <div className="ss-field" style={{ height: 44, padding: 0, marginBottom: 16 }}>
              <select value={showId} onChange={(e) => setShowId(e.target.value)} style={{ width: "100%", border: 0, background: "transparent", padding: "0 12px", font: "inherit", color: "inherit", outline: "none" }} data-testid="add-to-show-select">
                {shows.map((s) => <option key={s.id} value={s.id}>{s.title} {s.status === "live" ? "· LIVE" : "· scheduled"}</option>)}
              </select>
            </div>
            <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy} onClick={add} data-testid="add-to-show-confirm"><Radio size={16} /> {busy ? "Adding…" : "Add to show"}</button>
          </>
        )}
      </div>
    </div>
  );
}

function ImportModal({ onClose, onDone }) {
  const [tab, setTab] = useState("csv");
  const [sheetUrl, setSheetUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);

  const runCsv = async (text) => {
    setBusy(true); setResult(null);
    try { const r = await api.post("/seller/products/import", { csv: text }); setResult(r); onDone(); }
    catch (e) { toast.error(e.message || "Import failed"); } finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const text = await f.text();
    await runCsv(text);
  };
  const runSheet = async () => {
    if (!sheetUrl.trim()) return toast.error("Paste your Google Sheet link");
    setBusy(true); setResult(null);
    try { const r = await api.post("/seller/products/import-sheet", { sheet_url: sheetUrl.trim() }); setResult(r); onDone(); }
    catch (e) { toast.error(e.message || "Import failed"); } finally { setBusy(false); }
  };
  const downloadTemplate = async () => {
    const r = await api.get("/seller/products/import-template");
    const blob = new Blob([r.csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = r.filename; a.click();
  };

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="import-modal" style={{ maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 20 }}>Bulk import products</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="import-close"><X size={18} /></button>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <button className={`ss-chip ${tab === "csv" ? "active" : ""}`} onClick={() => setTab("csv")} data-testid="import-tab-csv"><Upload size={14} /> CSV file</button>
          <button className={`ss-chip ${tab === "sheet" ? "active" : ""}`} onClick={() => setTab("sheet")} data-testid="import-tab-sheet"><FileSpreadsheet size={14} /> Google Sheet</button>
        </div>
        <div className="ss-muted" style={{ fontSize: 13, marginBottom: 12 }}>
          Re-uploading updates the price & stock of items with a matching <b>SKU</b>, and adds any new ones.
          <button onClick={downloadTemplate} className="ss-btn ss-btn-ghost ss-btn-sm" style={{ marginLeft: 8 }} data-testid="import-template"><Download size={13} /> Download template</button>
        </div>
        {tab === "csv" ? (
          <div>
            <input ref={fileInput} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={onFile} data-testid="import-file-input" />
            <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy} onClick={() => fileInput.current?.click()} data-testid="import-file-pick">
              <Upload size={16} /> {busy ? "Importing…" : "Choose a .csv file"}
            </button>
          </div>
        ) : (
          <div>
            <label className="ss-label">Public Google Sheet link</label>
            <div className="ss-field" style={{ height: 46 }}><Link2 size={15} className="ss-muted" /><input placeholder="https://docs.google.com/spreadsheets/d/…" value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} data-testid="import-sheet-url" /></div>
            <div className="ss-muted" style={{ fontSize: 12, margin: "6px 0 12px" }}>Set the sheet to “Anyone with the link can view” (or File → Share → Publish to web).</div>
            <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy} onClick={runSheet} data-testid="import-sheet-go"><FileSpreadsheet size={16} /> {busy ? "Importing…" : "Import from Sheet"}</button>
          </div>
        )}
        {result && (
          <div className="ss-card" style={{ padding: 14, marginTop: 14 }} data-testid="import-result">
            <div style={{ fontWeight: 800 }}>✅ {result.created} added · {result.updated} updated</div>
            {result.error_count > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: "var(--red)", fontWeight: 700, fontSize: 13 }}>{result.error_count} row(s) skipped:</div>
                <ul className="ss-muted" style={{ fontSize: 12, margin: "4px 0 0 16px" }}>
                  {result.errors.slice(0, 8).map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StoreSyncModal({ conn, onClose, onChanged }) {
  const [platform, setPlatform] = useState("shopify");
  const [f, setF] = useState({ shop_domain: "", access_token: "", store_url: "", consumer_key: "", consumer_secret: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const connect = async () => {
    setBusy(true);
    try {
      const body = platform === "shopify"
        ? { platform, shop_domain: f.shop_domain, access_token: f.access_token }
        : { platform: "woocommerce", store_url: f.store_url, consumer_key: f.consumer_key, consumer_secret: f.consumer_secret };
      const r = await api.post("/seller/store/connect", body);
      toast.success(`Connected! ${r.result.created} added · ${r.result.updated} updated`);
      onChanged();
    } catch (e) { toast.error(e.message || "Couldn't connect"); } finally { setBusy(false); }
  };
  const syncNow = async () => {
    setBusy(true);
    try { const r = await api.post("/seller/store/sync"); toast.success(`Synced · ${r.result.created} added · ${r.result.updated} updated`); onChanged(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const toggle = async () => {
    try { await api.patch("/seller/store", { sync_enabled: !conn.sync_enabled }); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect this store? Synced products stay in your inventory.")) return;
    try { await api.del("/seller/store"); toast.success("Store disconnected"); onChanged(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="store-modal" style={{ maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 20 }}>Sync your store</h2>
          <button className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="store-close"><X size={18} /></button>
        </div>

        {conn ? (
          <div data-testid="store-connected">
            <div className="ss-card" style={{ padding: 16 }}>
              <div style={{ fontWeight: 800, textTransform: "capitalize", display: "flex", alignItems: "center", gap: 8 }}>
                <RefreshCw size={16} style={{ color: "var(--brand)" }} /> {conn.platform} connected
              </div>
              <div className="ss-muted" style={{ fontSize: 13, marginTop: 4 }}>{conn.shop_domain || conn.store_url} · key {conn.key_mask}</div>
              {conn.last_synced_at && <div className="ss-muted" style={{ fontSize: 12, marginTop: 2 }}>Last synced {new Date(conn.last_synced_at).toLocaleString()}{conn.last_result ? ` · ${conn.last_result.total} items` : ""}</div>}
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 14, fontWeight: 700 }}>
                <input type="checkbox" checked={conn.sync_enabled} onChange={toggle} data-testid="store-autosync" /> Auto-sync prices & stock (every 6 hours)
              </label>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <button className="ss-btn ss-btn-primary ss-btn-sm" disabled={busy} onClick={syncNow} data-testid="store-sync-now"><RefreshCw size={14} /> {busy ? "Syncing…" : "Sync now"}</button>
              <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)" }} onClick={disconnect} data-testid="store-disconnect">Disconnect</button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <button className={`ss-chip ${platform === "shopify" ? "active" : ""}`} onClick={() => setPlatform("shopify")} data-testid="store-platform-shopify">Shopify</button>
              <button className={`ss-chip ${platform === "woocommerce" ? "active" : ""}`} onClick={() => setPlatform("woocommerce")} data-testid="store-platform-woo">WooCommerce</button>
            </div>
            {platform === "shopify" ? (
              <>
                <label className="ss-label">Shop domain</label>
                <div className="ss-field" style={{ height: 46 }}><input placeholder="your-store.myshopify.com" value={f.shop_domain} onChange={set("shop_domain")} data-testid="store-shop-domain" /></div>
                <label className="ss-label" style={{ marginTop: 10 }}>Admin API access token</label>
                <div className="ss-field" style={{ height: 46 }}><input placeholder="shpat_…" value={f.access_token} onChange={set("access_token")} data-testid="store-shop-token" /></div>
                <div className="ss-muted" style={{ fontSize: 12, marginTop: 8 }}>Shopify admin → Settings → Apps → Develop apps → create an app with <b>read_products</b> + <b>read_inventory</b> → install → copy the Admin API access token.</div>
              </>
            ) : (
              <>
                <label className="ss-label">Store URL</label>
                <div className="ss-field" style={{ height: 46 }}><input placeholder="https://yourstore.com" value={f.store_url} onChange={set("store_url")} data-testid="store-woo-url" /></div>
                <label className="ss-label" style={{ marginTop: 10 }}>Consumer key</label>
                <div className="ss-field" style={{ height: 46 }}><input placeholder="ck_…" value={f.consumer_key} onChange={set("consumer_key")} data-testid="store-woo-ck" /></div>
                <label className="ss-label" style={{ marginTop: 10 }}>Consumer secret</label>
                <div className="ss-field" style={{ height: 46 }}><input placeholder="cs_…" value={f.consumer_secret} onChange={set("consumer_secret")} data-testid="store-woo-cs" /></div>
                <div className="ss-muted" style={{ fontSize: 12, marginTop: 8 }}>WooCommerce → Settings → Advanced → REST API → Add key with <b>Read</b> permission.</div>
              </>
            )}
            <button className="ss-btn ss-btn-primary ss-btn-block" style={{ marginTop: 16 }} disabled={busy} onClick={connect} data-testid="store-connect">{busy ? "Connecting & importing…" : "Connect & import"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Inventory() {
  const [products, setProducts] = useState(null);
  const [shows, setShows] = useState([]);
  const [me, setMe] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [creating, setCreating] = useState(false);
  const [addShowProduct, setAddShowProduct] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showStore, setShowStore] = useState(false);
  const [storeConn, setStoreConn] = useState(null);

  const load = () => Promise.all([api.get("/seller/products"), api.get("/shows"), api.get("/auth/me")])
    .then(([pr, sh, m]) => {
      setMe(m.user);
      setProducts(pr.products);
      setShows((sh.shows || []).filter((s) => s.seller_id === m.user?.user_id && ["live", "scheduled"].includes(s.status)));
    }).catch(() => setProducts([]));
  const loadStore = () => api.get("/seller/store").then((d) => setStoreConn(d.connection)).catch(() => {});
  useEffect(() => { load(); loadStore(); }, []);
  if (products === null) return <Loading />;

  const remove = async (id) => {
    if (!window.confirm("Delete this item from your inventory?")) return;
    try { await api.del(`/seller/products/${id}`); toast.success("Item deleted"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fade-up">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <PageHead title="Inventory" sub="Add your items with price, description and quantity — then drop them into a live or scheduled show." />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="ss-btn ss-btn-ghost" onClick={() => setShowStore(true)} data-testid="sync-store-btn">
            <Store size={16} /> {storeConn ? `${storeConn.platform} synced` : "Sync store"}
          </button>
          <button className="ss-btn ss-btn-ghost" onClick={() => setShowImport(true)} data-testid="import-products-btn"><Upload size={16} /> Import CSV</button>
          <button className="ss-btn ss-btn-primary" onClick={() => setCreating(true)} data-testid="new-inventory-item"><Plus size={16} /> New item</button>
        </div>
      </div>

      {storeConn && (
        <div data-testid="store-sync-status" onClick={() => setShowStore(true)}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer",
                   background: "var(--gold-soft)", border: "1px solid var(--line)", borderRadius: 999,
                   padding: "6px 14px", fontSize: 13, marginBottom: 16 }}>
          <RefreshCw size={14} style={{ color: storeConn.status === "error" ? "var(--red)" : "var(--brand)" }} />
          <span style={{ textTransform: "capitalize", fontWeight: 700 }}>{storeConn.platform}</span>
          {storeConn.status === "error" ? (
            <span style={{ color: "var(--red)" }}>· last sync failed — tap to retry</span>
          ) : (
            <span className="ss-muted">
              {storeConn.last_synced_at ? `· last synced ${timeAgo(storeConn.last_synced_at)}` : "· not synced yet"}
              {storeConn.last_result ? ` · ${storeConn.last_result.total} items` : ""}
              {storeConn.sync_enabled ? "" : " · auto-sync off"}
            </span>
          )}
        </div>
      )}
      {products.length === 0 ? (
        <Empty icon={Boxes} title="No items yet" sub="Tap “New item” to add your first product to inventory." />
      ) : (
        <div className="ss-card" style={{ overflow: "auto" }}>
          <table className="ss-table" data-testid="inventory-table">
            <thead><tr><th>Item</th><th>Type</th><th>Price</th><th>Qty</th><th>Package</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
            <tbody>
              {products.map((pr) => (
                <tr key={pr.id} data-testid={`inventory-row-${pr.id}`}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {pr.image
                        ? <img src={pr.image} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover" }} />
                        : <div style={{ width: 44, height: 44, borderRadius: 8, background: "var(--gold-soft)", display: "grid", placeItems: "center" }}><Boxes size={18} className="ss-muted" /></div>}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          {pr.title}
                          {pr.sku && <span className="ss-pill" style={{ fontSize: 11, fontWeight: 700 }} data-testid={`inv-sku-${pr.id}`}>SKU {pr.sku}</span>}
                          {pr.source && <span className="ss-pill pill--delivered" style={{ fontSize: 11 }} title={`Synced from ${pr.source}`}>{pr.source}</span>}
                        </div>
                        {pr.description && <div className="ss-muted" style={{ fontSize: 12, maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pr.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td>{pr.type === "auction" ? <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}><Gavel size={13} /> Auction</span> : <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}><ShoppingBag size={13} /> Buy now</span>}</td>
                  <td style={{ fontWeight: 700 }}>{money(pr.price)}</td>
                  <td><span className={`ss-pill ${pr.qty > 0 ? "pill--delivered" : "pill--open"}`} data-testid={`inv-qty-${pr.id}`}>{pr.qty} in stock</span></td>
                  <td className="ss-muted" style={{ fontSize: 13 }}>
                    {pr.package?.requires_shipping === false ? "No shipping" : `${pr.package?.weight ?? 1} lb · ${pr.package?.length ?? 8}×${pr.package?.width ?? 6}×${pr.package?.height ?? 4}"`}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button className="ss-btn ss-btn-sm ss-btn-primary" onClick={() => setAddShowProduct(pr)} data-testid={`add-to-show-${pr.id}`}><Radio size={13} /> Add to show</button>
                      <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => setEditProduct(pr)} data-testid={`edit-product-${pr.id}`}><Pencil size={13} /></button>
                      <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)" }} onClick={() => remove(pr.id)} data-testid={`delete-product-${pr.id}`}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editProduct) && (
        <ProductModal product={editProduct} onClose={() => { setCreating(false); setEditProduct(null); }} onSaved={() => { setCreating(false); setEditProduct(null); load(); }} />
      )}
      {addShowProduct && (
        <AddToShowModal product={addShowProduct} shows={shows} onClose={() => setAddShowProduct(null)} onAdded={() => { setAddShowProduct(null); load(); }} />
      )}
      {showImport && (
        <ImportModal onClose={() => setShowImport(false)} onDone={load} />
      )}
      {showStore && (
        <StoreSyncModal conn={storeConn} onClose={() => setShowStore(false)} onChanged={() => { loadStore(); load(); }} />
      )}
    </div>
  );
}
